import { Box, Stack, Typography, useTheme } from "@mui/material";
import { Button, Drawer, InfoIcon } from "components";
import { useCallback, useMemo, useState } from "react";
import { KbImportFileList } from "./KbImportFileList";
import { KbImportProgressState } from "./KbImportProgressState";
import { KbImportUploadZone } from "./KbImportUploadZone";
import { useUploadKbFiles } from "controller";
import toast from "react-hot-toast";
import { useAtomValue } from "jotai";
import { centralFolderList } from "../store";
import { addMixpanelEvent } from "utilz/helpers";

interface KbImportDrawerProps {
  open: boolean;
  onClose: () => void;
  folderId?: string | null;
}

type UploadState = "idle" | "uploading" | "success" | "error";

const MAX_FILES = 5;

export const KbImportDrawer = ({
  open,
  onClose,
  folderId,
}: KbImportDrawerProps) => {
  const theme = useTheme();
  const centralKbFolder = useAtomValue(centralFolderList);
  const [files, setFiles] = useState<File[]>([]);
  const [uploadState, setUploadState] = useState<UploadState>("idle");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);

  const { mutateAsync: uploadFiles, isLoading } = useUploadKbFiles();

  const handleFilesSelected = useCallback((newFiles: File[]) => {
    setFiles((prev) => {
      const combined = [...prev, ...newFiles];
      // Ensure we don't exceed max files
      return combined.slice(0, MAX_FILES);
    });
  }, []);

  const handleRemoveFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleUpload = useCallback(async () => {
    if (files.length === 0) return;

    try {
      setUploadState("uploading");

      // Calculate total bytes
      const total = files.reduce((sum, file) => sum + file.size, 0);
      setTotalBytes(total);
      setUploadedBytes(0);
      setUploadProgress(0);

      // Upload files with progress tracking
      await uploadFiles({
        files,
        folderId: folderId || centralKbFolder?.[0]?.mapping_id,
        onProgress: (progress, uploaded, total) => {
          setUploadProgress(progress);
          setUploadedBytes(uploaded);
          setTotalBytes(total);
        },
      });

      setUploadState("success");
      addMixpanelEvent({
        event: "kb_article_imported",
        data: {
          fileCount: files?.length,
          folderId: folderId || centralKbFolder?.[0]?.mapping_id,
        },
      });
      toast.success("Files uploaded successfully");
    } catch (error) {
      console.error("Upload error:", error);
      setUploadState("error");
      toast.error("Failed to upload files");
    }
  }, [files, folderId, uploadFiles]);

  const handleClose = useCallback(() => {
    if (uploadState === "uploading") {
      // Don't allow closing during upload
      return;
    }

    // Reset state
    setFiles([]);
    setUploadState("idle");
    setUploadProgress(0);
    setUploadedBytes(0);
    setTotalBytes(0);

    onClose();
  }, [uploadState, onClose]);

  const primaryButtonText = useMemo(() => {
    if (uploadState === "success" || uploadState === "error") {
      return "Close";
    }
    return "Upload files";
  }, [uploadState]);

  const isPrimaryButtonDisabled = useMemo(() => {
    if (uploadState === "uploading") return true;
    if (uploadState === "idle" && files.length === 0) return true;
    return false;
  }, [uploadState, files.length]);

  // const showSecondaryButton = useMemo(() => {
  //   return uploadState === "idle";
  // }, [uploadState]);

  const handlePrimaryButtonClick = useCallback(() => {
    if (uploadState === "success" || uploadState === "error") {
      handleClose();
    } else if (uploadState === "idle") {
      handleUpload();
    }
  }, [uploadState, handleClose, handleUpload]);

  const handleSecondaryButtonClick = useCallback(() => {
    setFiles([]);
  }, []);

  const renderContent = () => {
    if (uploadState === "uploading") {
      return (
        <KbImportProgressState
          fileCount={files.length}
          progress={uploadProgress}
          uploadedBytes={uploadedBytes}
          totalBytes={totalBytes}
        />
      );
    }

    if (uploadState === "success") {
      const totalSize = files.reduce((sum, file) => sum + file.size, 0);
      const totalSizeKb = Math.round(totalSize / 1024);

      return (
        <Stack
          sx={{
            padding: "24px",
            gap: "24px",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "200px",
          }}
        >
          <Stack
            sx={{
              gap: "4px",
              width: "100%",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Files uploaded successfully</Typography>
            <Typography
              variant="body-small"
              sx={{
                color: theme.palette.lightBg.low,
              }}
            >
              {files.length} file{files.length !== 1 ? "s" : ""}, {totalSizeKb}{" "}
              kb
            </Typography>
          </Stack>

          <Stack
            direction="row"
            sx={{
              gap: "12px",
              padding: "16px",
              backgroundColor: theme.palette.primitive.system.blue[25],
              borderRadius: "8px",
              border: `1px solid ${theme.palette.primitive.system.blue[400]}`,
            }}
          >
            <InfoIcon
              sx={{
                fontSize: "20px",
                color: theme.palette.primitive.system.blue[800],
                flexShrink: 0,
              }}
            />
            <Typography
              variant="body-small"
              sx={{
                color: theme.palette.lightBg.high,
              }}
            >
              Articles will reflect in the DeskDay Knowledge base within 2-5
              minutes.
              <br />
              <br />
              The uploaded articles will be added to the current folder as
              Draft.
            </Typography>
          </Stack>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Stack>
      );
    }

    if (uploadState === "error") {
      const totalSize = files.reduce((sum, file) => sum + file.size, 0);
      const totalSizeKb = Math.round(totalSize / 1024);

      return (
        <Stack
          sx={{
            padding: "24px",
            gap: "24px",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Stack
            sx={{
              gap: "4px",
              width: "100%",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Upload failed</Typography>
            <Typography
              variant="body-small"
              sx={{
                color: theme.palette.lightBg.low,
              }}
            >
              {files.length} file{files.length !== 1 ? "s" : ""}, {totalSizeKb}{" "}
              kb
            </Typography>
          </Stack>

          <Stack
            direction="row"
            sx={{
              gap: "12px",
              padding: "16px",
              backgroundColor: theme.palette.primitive.system.red[50],
              borderRadius: "8px",
              border: `1px solid ${theme.palette.primitive.system.red[400]}`,
            }}
          >
            <InfoIcon
              sx={{
                fontSize: "20px",
                color: theme.palette.error.main,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="body-small"
              sx={{
                color: theme.palette.lightBg.high,
              }}
            >
              Something went wrong while uploading your files. Please try again
              in a few minutes.
            </Typography>
          </Stack>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Stack>
      );
    }

    // Idle state
    return (
      <Stack
        sx={{
          padding: "24px",
          gap: "16px",
        }}
      >
        <Typography
          variant="body-medium"
          sx={{
            color: theme.palette.lightBg.low,
          }}
        >
          Upload documents to automatically create knowledge base articles.
          Supported formats: <strong>.DOCX, .PDF, .MD, .TXT</strong> (up to{" "}
          <strong>5</strong> files per upload).
        </Typography>
        <Typography
          variant="body-medium"
          sx={{
            color: theme.palette.lightBg.low,
          }}
        >
          Imported articles are saved as <strong>Drafts</strong> and usually
          take 2–5 minutes to process.
        </Typography>

        <KbImportUploadZone
          onFilesSelected={handleFilesSelected}
          maxFiles={MAX_FILES}
          currentFileCount={files.length}
          disabled={files.length >= 5 ? true : false}
        />

        <KbImportFileList
          files={files}
          onRemoveFile={handleRemoveFile}
          disabled={false}
        />
      </Stack>
    );
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      onOpen={() => {}}
      title={"Import knowledge base articles"}
      primaryButtonText={primaryButtonText}
      secondaryButtonText="Clear All"
      onPrimaryButtonClick={handlePrimaryButtonClick}
      onSecondaryButtonClick={handleSecondaryButtonClick}
      isPrimaryButtonDisabled={isPrimaryButtonDisabled}
      isSecondaryButtonDisabled={uploadState !== "idle" || files.length === 0}
      isLoading={isLoading}
      drawerStyle={{
        width: "400px",
        maxWidth: "90vw",
      }}
      showHeaderCloseBtn={uploadState !== "uploading"}
      disableCloseOnBackdropClick={uploadState === "uploading"}
      secondaryButtonSx={{
        display: "none",
      }}
      primaryButtonSx={{
        width: "100%",
      }}
      footer={
        <>
          {uploadState === "idle" ? (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                width: "100%",
                padding: "12px 16px",
                borderTop: `1px solid ${theme.palette.neutral["020"]}`,
              }}
            >
              <Button onClick={handlePrimaryButtonClick} fullWidth>
                Upload files
              </Button>
            </Stack>
          ) : null}
        </>
      }
    >
      {renderContent()}
    </Drawer>
  );
};
